import React from 'react';
import RadioButton, {
  RadioButtonProps,
} from '@wix/da-a11y/pkg/components/formControls/RadioButton';
import { CommonFacetAPI, useFacet } from '../../common';
import FacetContent from '../../lego/FacetContent';

interface PropsWithoutChildren
  extends CommonFacetAPI,
    Omit<RadioButtonProps, 'checked' | 'children' | 'size'> {
  children?: string;
  checked?: boolean;
}

export interface Props extends PropsWithoutChildren {
  children: string;
}

const FacetRadioButton: React.FC<Props> = ({
  children,
  ...unprocessedProps
}) => {
  const {
    rootComponentProps,
    contentComponentProps,
    implementationSpecificProps: { checked, ...implementationSpecificProps },
    /**
     * We allow `selected` or `checked` in this Facet implementation to better mesh with the react-forms library.
     * Since react-forms expects every component to have native fields, `selected` alone won't work for it. However, we
     * can't use `checked` alone because the DS component in figma has a `selected` prop. Deviating from that
     * would add a layer of confusion to the rest of the Facet implementations that do not require this
     * `checked` prop on their root component.
     *
     * This is a necessary evil.
     */
  } = useFacet<PropsWithoutChildren>({
    ...unprocessedProps,
    selected: unprocessedProps.selected ?? unprocessedProps.checked,
  });

  return (
    <RadioButton
      // requiring `children: string` in our props so we can force the consumer to provide the necessary text
      hasAccessibleText
      checked={!!(unprocessedProps.selected ?? checked)}
      {...rootComponentProps}
      {...implementationSpecificProps}
    >
      <FacetContent {...contentComponentProps}>{children}</FacetContent>
    </RadioButton>
  );
};

FacetRadioButton.displayName = 'FacetRadioButton';

export default FacetRadioButton;
